const MAX_CLIENT_ERROR_CODES = 3;
const ERROR_NAME_PATTERN = /^[A-Za-z][A-Za-z0-9_.-]{0,63}$/;
const ERROR_CODE_PATTERN = /^[A-Za-z0-9][A-Za-z0-9_.-]{0,31}$/;

const recentClientErrorCodes: string[] = [];

function safeProperty(value: unknown, key: "name" | "code"): unknown {
  if (value === null || typeof value !== "object") return undefined;
  try {
    return (value as Record<string, unknown>)[key];
  } catch {
    return undefined;
  }
}

function normalizedName(value: unknown, fallback: string): string {
  return typeof value === "string" && ERROR_NAME_PATTERN.test(value) ? value : fallback;
}

function normalizedCode(value: unknown): string | null {
  const candidate =
    typeof value === "number" && Number.isFinite(value) ? String(value) : value;
  return typeof candidate === "string" && ERROR_CODE_PATTERN.test(candidate)
    ? candidate
    : null;
}

/**
 * Records only a closed name/code token. Messages, stacks, URLs, payloads, and arbitrary values
 * never enter the feedback context buffer.
 */
export function recordClientErrorCode(name: unknown, code?: unknown): void {
  const safeName = normalizedName(name, "ClientError");
  const safeCode = normalizedCode(code);
  const value = safeCode === null ? safeName : `${safeName}:${safeCode}`;
  if (recentClientErrorCodes.at(-1) === value) return;
  recentClientErrorCodes.push(value);
  if (recentClientErrorCodes.length > MAX_CLIENT_ERROR_CODES) {
    recentClientErrorCodes.splice(0, recentClientErrorCodes.length - MAX_CLIENT_ERROR_CODES);
  }
}

export function getRecentClientErrorCodes(): readonly string[] {
  return [...recentClientErrorCodes];
}

export function clearRecentClientErrorCodesForTest(): void {
  recentClientErrorCodes.splice(0, recentClientErrorCodes.length);
}

/** Registers this-tab browser listeners. Call once from the authenticated Console shell. */
export function startClientErrorCodeCapture(target: Window): () => void {
  const onError = (event: ErrorEvent): void => {
    const error = event.error;
    recordClientErrorCode(
      safeProperty(error, "name") ?? "WindowError",
      safeProperty(error, "code"),
    );
  };
  const onUnhandledRejection = (event: PromiseRejectionEvent): void => {
    recordClientErrorCode(
      safeProperty(event.reason, "name") ?? "UnhandledRejection",
      safeProperty(event.reason, "code"),
    );
  };

  target.addEventListener("error", onError);
  target.addEventListener("unhandledrejection", onUnhandledRejection);
  return () => {
    target.removeEventListener("error", onError);
    target.removeEventListener("unhandledrejection", onUnhandledRejection);
  };
}
